import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import { getHeaders } from '../../utility/getHeader';
import fetchData from '../../functions/fetchData';
import Layout from '../../layout/Layout';
import Header from '../../components/Header';
import { FaPlus, FaEdit, FaTrash, FaEye, FaUniversity, FaWallet, FaMoneyBillWave } from 'react-icons/fa';
import './bankManagement.css';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:3001';
const ENDPOINTS = {
    BANK_ACCOUNTS: `${API_BASE_URL}/api/bank-accounts`,
    BANK_ACCOUNT_BY_ID: (id) => `${API_BASE_URL}/api/bank-accounts/${id}`,
    TOGGLE_STATUS: (id) => `${API_BASE_URL}/api/bank-accounts/${id}/toggle-status`
};

// Modal component moved outside to prevent re-creation on each render
const AccountModal = ({ show, onHide, title, formData, setFormData, handleSubmit, accountTypes }) => {
    if (!show) return null;

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-header bg-primary text-white">
                        <h5 className="modal-title">{title}</h5>
                        <button type="button" className="btn-close btn-close-white" onClick={onHide}></button>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Account Name <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formData.account_name}
                                        onChange={(e) => setFormData({...formData, account_name: e.target.value})}
                                        required
                                        autoFocus
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Bank Name <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formData.bank_name}
                                        onChange={(e) => setFormData({...formData, bank_name: e.target.value})}
                                        required
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Account Number <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formData.account_number}
                                        onChange={(e) => setFormData({...formData, account_number: e.target.value})}
                                        required
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Account Type</label>
                                    <select
                                        className="form-control"
                                        value={formData.account_type}
                                        onChange={(e) => setFormData({...formData, account_type: e.target.value})}
                                    >
                                        {accountTypes.map(type => (
                                            <option key={type} value={type}>{type}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Branch Name</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formData.branch_name}
                                        onChange={(e) => setFormData({...formData, branch_name: e.target.value})}
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">IFSC Code</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formData.ifsc_code}
                                        onChange={(e) => setFormData({...formData, ifsc_code: e.target.value})}
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">SWIFT Code</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formData.swift_code}
                                        onChange={(e) => setFormData({...formData, swift_code: e.target.value})}
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Opening Balance</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        className="form-control"
                                        value={formData.opening_balance}
                                        onChange={(e) => setFormData({...formData, opening_balance: parseFloat(e.target.value) || 0})}
                                    />
                                </div>
                                <div className="col-12 mb-3">
                                    <label className="form-label">Notes</label>
                                    <textarea
                                        className="form-control"
                                        rows="3"
                                        value={formData.notes}
                                        onChange={(e) => setFormData({...formData, notes: e.target.value})}
                                        placeholder="Additional notes or comments"
                                    ></textarea>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={onHide}>
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-primary">
                                {title.includes('Edit') ? 'Update Account' : 'Add Account'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default function BankManagement() {
    const [bankAccounts, setBankAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [formData, setFormData] = useState({
        account_name: '',
        bank_name: '',
        account_number: '',
        account_type: 'Savings',
        branch_name: '',
        ifsc_code: '',
        swift_code: '',
        opening_balance: 0,
        notes: ''
    });

    const accountTypes = ['Savings', 'Current', 'Credit', 'Cash', 'UPI'];

    useEffect(() => {
        fetchBankAccounts();
    }, []);

    const fetchBankAccounts = async () => {
        setLoading(true);
        try {
            await fetchData("bank_accounts", setBankAccounts, "id", {});
        } catch (error) {
            console.error('Error fetching bank accounts:', error);
            toast.error('Failed to fetch bank accounts');
        }
        setLoading(false);
    };

    const resetForm = () => {
        setFormData({
            account_name: '',
            bank_name: '',
            account_number: '',
            account_type: 'Savings',
            branch_name: '',
            ifsc_code: '',
            swift_code: '',
            opening_balance: 0,
            notes: ''
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const requestData = {
                ...formData,
                current_balance: selectedAccount ? selectedAccount.current_balance : formData.opening_balance
            };

            let response;
            if (selectedAccount) {
                // Update existing account
                response = await axios.put(
                    ENDPOINTS.BANK_ACCOUNT_BY_ID(selectedAccount.id),
                    requestData,
                    getHeaders()
                );
            } else {
                // Create new account using insertdata API
                response = await axios.post(
                    `/insertdata/bank_accounts`,
                    requestData,
                    getHeaders()
                );
            }

            if (response.data.success) {
                toast.success(`Bank account ${selectedAccount ? 'updated' : 'created'} successfully`);
                fetchBankAccounts();
                setShowAddModal(false);
                setShowEditModal(false);
                resetForm();
                setSelectedAccount(null);
            }
        } catch (error) {
            console.error('Error saving bank account:', error);
            toast.error(error.response?.data?.message || 'Failed to save bank account');
        }
        setLoading(false);
    };

    const handleEdit = (account) => {
        setSelectedAccount(account);
        setFormData({
            account_name: account.account_name || '',
            bank_name: account.bank_name || '',
            account_number: account.account_number || '',
            account_type: account.account_type || 'Savings',
            branch_name: account.branch_name || '',
            ifsc_code: account.ifsc_code || '',
            swift_code: account.swift_code || '',
            opening_balance: account.opening_balance || 0,
            notes: account.notes || ''
        });
        setShowEditModal(true);
    };

    const handleDelete = async (account) => {
        if (!window.confirm(`Are you sure you want to delete "${account.account_name}"?`)) {
            return;
        }

        setLoading(true);
        try {
            const response = await axios.delete(
                `/deletebyid/bank_accounts/id/${account.id}`,
                getHeaders()
            );

            if (response.data.success) {
                toast.success('Bank account deleted successfully');
                fetchBankAccounts();
            } else {
                toast.error(response.data.message || 'Failed to delete bank account');
            }
        } catch (error) {
            console.error('Error deleting bank account:', error);
            toast.error(error.response?.data?.message || 'Failed to delete bank account');
        }
        setLoading(false);
    };

    const toggleStatus = async (account) => {
        setLoading(true);
        try {
            const response = await axios.put(
                ENDPOINTS.TOGGLE_STATUS(account.id),
                {},
                getHeaders()
            );

            if (response.data.success) {
                toast.success(`Account ${account.is_active ? 'deactivated' : 'activated'} successfully`);
                fetchBankAccounts();
            }
        } catch (error) {
            console.error('Error toggling account status:', error);
            toast.error('Failed to update account status');
        }
        setLoading(false);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('th-TH', {
            style: 'currency',
            currency: 'THB'
        }).format(amount);
    };

    const getAccountIcon = (accountType) => {
        switch (accountType) {
            case 'Cash': return <FaMoneyBillWave className="text-success" />;
            case 'UPI': return <FaWallet className="text-info" />;
            default: return <FaUniversity className="text-primary" />;
        }
    };

    return (
        <Layout>
            <Header title="Bank Management" />
            <div className="container-fluid p-4">
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <h4 className="mb-0">
                                <FaUniversity className="me-2" />
                                Bank Accounts
                            </h4>
                            <button 
                                className="btn btn-primary"
                                onClick={() => {
                                    resetForm();
                                    setSelectedAccount(null);
                                    setShowAddModal(true);
                                }}
                            >
                                <FaPlus className="me-2" />
                                Add Bank Account
                            </button>
                        </div>
                    </div>
                </div>

                {/* Summary Cards */}
                <div className="row mb-4">
                    <div className="col-md-3 mb-3">
                        <div className="card bg-primary text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between">
                                    <div>
                                        <h6 className="card-title">Total Accounts</h6>
                                        <h4>{bankAccounts.length}</h4>
                                    </div>
                                    <FaUniversity size={40} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3 mb-3">
                        <div className="card bg-success text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between">
                                    <div>
                                        <h6 className="card-title">Active Accounts</h6>
                                        <h4>{bankAccounts.filter(acc => acc.is_active).length}</h4>
                                    </div>
                                    <FaEye size={40} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3 mb-3">
                        <div className="card bg-info text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between">
                                    <div>
                                        <h6 className="card-title">Total Balance</h6>
                                        <h4>{formatCurrency(bankAccounts.reduce((sum, acc) => sum + (parseFloat(acc.current_balance) || 0), 0))}</h4>
                                    </div>
                                    <FaWallet size={40} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3 mb-3">
                        <div className="card bg-warning text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between">
                                    <div>
                                        <h6 className="card-title">Cash Accounts</h6>
                                        <h4>{bankAccounts.filter(acc => acc.account_type === 'Cash').length}</h4>
                                    </div>
                                    <FaMoneyBillWave size={40} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bank Accounts Table */}
                <div className="card">
                    <div className="card-header">
                        <h5 className="card-title mb-0">Bank Accounts List</h5>
                    </div>
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-4">
                                <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : bankAccounts.length === 0 ? (
                            <div className="text-center py-4">
                                <p className="text-muted mb-0">No bank accounts found. Add your first bank account to get started.</p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-striped table-hover">
                                    <thead className="table-dark">
                                        <tr>
                                            <th>Account</th>
                                            <th>Bank</th>
                                            <th>Account Number</th>
                                            <th>Type</th>
                                            <th>Current Balance</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {bankAccounts.map((account) => (
                                            <tr key={account.id}>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        {getAccountIcon(account.account_type)}
                                                        <div className="ms-2">
                                                            <div className="fw-semibold">{account.account_name}</div>
                                                            {account.branch_name && <small className="text-muted">{account.branch_name}</small>}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>{account.bank_name}</td>
                                                <td className="font-monospace">{account.account_number}</td>
                                                <td>
                                                    <span className={`badge ${account.account_type === 'Cash' ? 'bg-success' : 'bg-primary'}`}>
                                                        {account.account_type}
                                                    </span>
                                                </td>
                                                <td className="fw-semibold">{formatCurrency(parseFloat(account.current_balance) || 0)}</td>
                                                <td>
                                                    <span className={`badge ${account.is_active ? 'bg-success' : 'bg-danger'}`}>
                                                        {account.is_active ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="btn-group btn-group-sm">
                                                        <button 
                                                            className="btn btn-outline-primary"
                                                            onClick={() => handleEdit(account)}
                                                            title="Edit"
                                                        >
                                                            <FaEdit />
                                                        </button>
                                                        <button 
                                                            className={`btn ${account.is_active ? 'btn-outline-warning' : 'btn-outline-success'}`}
                                                            onClick={() => toggleStatus(account)}
                                                            title={account.is_active ? 'Deactivate' : 'Activate'}
                                                        >
                                                            <FaEye />
                                                        </button>
                                                        <button 
                                                            className="btn btn-outline-danger"
                                                            onClick={() => handleDelete(account)}
                                                            title="Delete"
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Add Modal */}
            <AccountModal 
                show={showAddModal} 
                onHide={() => {setShowAddModal(false); resetForm();}} 
                title="Add Bank Account"
                formData={formData}
                setFormData={setFormData}
                handleSubmit={handleSubmit}
                accountTypes={accountTypes}
            />

            {/* Edit Modal */}
            <AccountModal 
                show={showEditModal} 
                onHide={() => {setShowEditModal(false); resetForm(); setSelectedAccount(null);}} 
                title="Edit Bank Account"
                formData={formData}
                setFormData={setFormData}
                handleSubmit={handleSubmit}
                accountTypes={accountTypes}
            />

            <ToastContainer />
        </Layout>
    );
}